import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Loader2, Play, Plus, ToggleLeft, ToggleRight, Trash2 } from 'lucide-react';
import { Button } from '@/components/Button';
import { Card, CardBody } from '@/components/Card';
import { Skeleton, SkeletonRegion } from '@/components/Skeleton';
import { formatarDataHora, formatarDistancia } from '@/lib/format';
import {
  chavesCmdb,
  confirmarDrift,
  criarFonte,
  excluirFonte,
  iniciarRun,
  listarCandidatos,
  listarDrifts,
  listarFontes,
  listarRuns,
  resolverCandidato,
  atualizarFonte,
  type CreateSourceDto,
  type DiscoverySource,
  type DiscoveryRun,
} from '@/features/cmdb/cmdb.api';

const TIPO_PT: Record<string, string> = { ESXI: 'ESXi / vCenter', WAZUH: 'Wazuh' };

const STATUS_COR: Record<string, string> = {
  RUNNING: 'text-primary',
  COMPLETED: 'text-success',
  FAILED: 'text-destructive',
  CANCELLED: 'text-muted-foreground',
};

const STATUS_PT: Record<string, string> = {
  RUNNING: 'Executando',
  COMPLETED: 'Concluído',
  FAILED: 'Falhou',
  CANCELLED: 'Cancelado',
};

export function CmdbPage() {
  const qc = useQueryClient();
  const [aba, setAba] = useState<'sources' | 'drifts' | 'candidates'>('sources');
  const [fonteAberta, setFonteAberta] = useState<string | null>(null);
  const [criando, setCriando] = useState(false);

  const { data: fontes, isPending: carregandoFontes } = useQuery({
    queryKey: chavesCmdb.fontes(),
    queryFn: listarFontes,
  });

  const { data: drifts, isPending: carregandoDrifts } = useQuery({
    queryKey: chavesCmdb.drifts(),
    queryFn: listarDrifts,
    enabled: aba === 'drifts',
  });

  const { data: candidatos, isPending: carregandoCandidatos } = useQuery({
    queryKey: chavesCmdb.candidatos(),
    queryFn: listarCandidatos,
    enabled: aba === 'candidates',
  });

  const criarMutacao = useMutation({
    mutationFn: (dto: CreateSourceDto) => criarFonte(dto),
    onSuccess: () => {
      setCriando(false);
      void qc.invalidateQueries({ queryKey: chavesCmdb.fontes() });
    },
  });

  const excluirMutacao = useMutation({
    mutationFn: (id: string) => excluirFonte(id),
    onSuccess: () => void qc.invalidateQueries({ queryKey: chavesCmdb.fontes() }),
  });

  const toggleMutacao = useMutation({
    mutationFn: ({ id, enabled }: { id: string; enabled: boolean }) =>
      atualizarFonte(id, { enabled }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: chavesCmdb.fontes() }),
  });

  const driftMutacao = useMutation({
    mutationFn: (id: string) => confirmarDrift(id),
    onSuccess: () => void qc.invalidateQueries({ queryKey: chavesCmdb.drifts() }),
  });

  const candidatoMutacao = useMutation({
    mutationFn: ({ id, action }: { id: string; action: 'MERGED' | 'IGNORED' }) =>
      resolverCandidato(id, action),
    onSuccess: () => void qc.invalidateQueries({ queryKey: chavesCmdb.candidatos() }),
  });

  const abas = [
    { key: 'sources' as const, label: 'Fontes de descoberta' },
    { key: 'drifts' as const, label: `Desvios${drifts?.length ? ` (${drifts.length})` : ''}` },
    { key: 'candidates' as const, label: `Candidatos a mescla${candidatos?.length ? ` (${candidatos.length})` : ''}` },
  ];

  return (
    <div className="mx-auto max-w-[1400px] space-y-3">
      <header className="flex items-center justify-between gap-4 border-b border-border pb-3">
        <h1 className="text-base font-semibold">CMDB — Descoberta automática</h1>
        {aba === 'sources' && (
          <Button onClick={() => setCriando(true)} disabled={criando} icon={<Plus className="h-4 w-4" aria-hidden />}>
            Nova fonte
          </Button>
        )}
      </header>

      <div className="flex gap-1 border-b border-border">
        {abas.map((a) => (
          <button
            key={a.key}
            type="button"
            onClick={() => setAba(a.key)}
            className={`relative px-4 py-2.5 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:shadow-glow ${
              aba === a.key
                ? 'text-primary'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            {a.label}
            {aba === a.key && <span className="absolute inset-x-2 -bottom-px h-0.5 rounded-full bg-primary" />}
          </button>
        ))}
      </div>

      {aba === 'sources' && (
        <div className="space-y-3">
          {criando && (
            <NovaFonteForm
              onSave={(dto) => criarMutacao.mutate(dto)}
              onCancel={() => setCriando(false)}
              salvando={criarMutacao.isPending}
            />
          )}

          {carregandoFontes ? (
            <SkeletonRegion label="Carregando fontes">
              <Skeleton className="h-24 w-full rounded-lg" />
              <Skeleton className="h-24 w-full rounded-lg" />
            </SkeletonRegion>
          ) : !fontes?.length ? (
            <Card>
              <CardBody className="py-8 text-center text-sm text-muted-foreground">
                Nenhuma fonte configurada. Adicione ESXi ou Wazuh para começar.
              </CardBody>
            </Card>
          ) : (
            fontes.map((fonte) => (
              <div key={fonte.id}>
                <FonteCard
                  fonte={fonte}
                  expandido={fonteAberta === fonte.id}
                  onToggleExpand={() => setFonteAberta(fonteAberta === fonte.id ? null : fonte.id)}
                  onDelete={() => excluirMutacao.mutate(fonte.id)}
                  onToggleEnabled={() => toggleMutacao.mutate({ id: fonte.id, enabled: !fonte.enabled })}
                  deletando={excluirMutacao.isPending}
                />
                {fonteAberta === fonte.id && <RunsPanel sourceId={fonte.id} />}
              </div>
            ))
          )}
        </div>
      )}

      {aba === 'drifts' && (
        <div className="space-y-2">
          {carregandoDrifts ? (
            <SkeletonRegion label="Carregando desvios">
              <Skeleton className="h-16 w-full rounded-lg" />
            </SkeletonRegion>
          ) : !drifts?.length ? (
            <Card>
              <CardBody className="py-8 text-center text-sm text-muted-foreground">
                Nenhum desvio pendente.
              </CardBody>
            </Card>
          ) : (
            drifts.map((drift) => (
              <Card key={drift.id}>
                <CardBody className="py-3">
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div className="space-y-0.5">
                      <div className="flex items-center gap-2">
                        <AlertTriangle className="h-3.5 w-3.5 text-warning" aria-hidden />
                        <span className="text-sm font-medium text-foreground">{drift.attrKey}</span>
                        <span className="text-xs text-muted-foreground">{formatarDistancia(drift.detectedAt)}</span>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        <span className="line-through">{drift.oldValue ?? '—'}</span>
                        {' → '}
                        <span>{drift.newValue ?? '—'}</span>
                      </p>
                    </div>
                    <Button
                      size="sm"
                      variant="secondary"
                      disabled={driftMutacao.isPending}
                      onClick={() => driftMutacao.mutate(drift.id)}
                    >
                      Reconhecer
                    </Button>
                  </div>
                </CardBody>
              </Card>
            ))
          )}
        </div>
      )}

      {aba === 'candidates' && (
        <div className="space-y-2">
          {carregandoCandidatos ? (
            <SkeletonRegion label="Carregando candidatos">
              <Skeleton className="h-16 w-full rounded-lg" />
            </SkeletonRegion>
          ) : !candidatos?.length ? (
            <Card>
              <CardBody className="py-8 text-center text-sm text-muted-foreground">
                Nenhum candidato a mescla pendente.
              </CardBody>
            </Card>
          ) : (
            candidatos.map((c) => (
              <Card key={c.id}>
                <CardBody className="py-3">
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div className="space-y-0.5">
                      <p className="text-sm font-medium text-foreground">
                        Confiança: {c.confidence}%
                      </p>
                      <p className="text-xs text-muted-foreground">{c.reason}</p>
                    </div>
                    <div className="flex gap-2 shrink-0">
                      <Button
                        size="sm"
                        disabled={candidatoMutacao.isPending}
                        onClick={() => candidatoMutacao.mutate({ id: c.id, action: 'MERGED' })}
                      >
                        Mesclar
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        disabled={candidatoMutacao.isPending}
                        onClick={() => candidatoMutacao.mutate({ id: c.id, action: 'IGNORED' })}
                      >
                        Ignorar
                      </Button>
                    </div>
                  </div>
                </CardBody>
              </Card>
            ))
          )}
        </div>
      )}
    </div>
  );
}

function FonteCard({
  fonte,
  expandido,
  onToggleExpand,
  onDelete,
  onToggleEnabled,
  deletando,
}: {
  fonte: DiscoverySource;
  expandido: boolean;
  onToggleExpand: () => void;
  onDelete: () => void;
  onToggleEnabled: () => void;
  deletando: boolean;
}) {
  return (
    <Card>
      <CardBody className="flex flex-row items-center justify-between gap-3 py-3">
        <button
          type="button"
          onClick={onToggleExpand}
          className="flex-1 text-left"
          aria-expanded={expandido}
        >
          <div className="flex flex-wrap items-center gap-3">
            <span className="text-sm font-semibold text-foreground">{fonte.name}</span>
            <span className="rounded bg-muted px-2 py-0.5 text-xs text-muted-foreground">
              {TIPO_PT[fonte.type] ?? fonte.type}
            </span>
            {!fonte.enabled && (
              <span className="rounded bg-yellow-50 px-2 py-0.5 text-xs text-yellow-700 dark:bg-yellow-900/20 dark:text-yellow-400">
                Desativada
              </span>
            )}
            {fonte.lastRunAt && (
              <span className="text-xs text-muted-foreground">
                Último run: {formatarDistancia(fonte.lastRunAt)}
              </span>
            )}
          </div>
          <p className="mt-0.5 truncate text-xs text-muted-foreground">{fonte.endpoint}</p>
        </button>
        <div className="flex gap-1 shrink-0">
          <button
            type="button"
            onClick={onToggleEnabled}
            className="rounded p-1.5 text-muted-foreground hover:bg-muted"
            title={fonte.enabled ? 'Desativar' : 'Ativar'}
          >
            {fonte.enabled ? (
              <ToggleRight className="h-4 w-4 text-green-600 dark:text-green-400" aria-hidden />
            ) : (
              <ToggleLeft className="h-4 w-4" aria-hidden />
            )}
          </button>
          <button
            type="button"
            onClick={onDelete}
            disabled={deletando}
            className="rounded p-1.5 text-muted-foreground hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-900/20"
            title="Excluir fonte"
          >
            <Trash2 className="h-4 w-4" aria-hidden />
          </button>
        </div>
      </CardBody>
    </Card>
  );
}

function RunsPanel({ sourceId }: { sourceId: string }) {
  const qc = useQueryClient();
  const { data: runs, isPending } = useQuery({
    queryKey: chavesCmdb.runs(sourceId),
    queryFn: () => listarRuns(sourceId),
    refetchInterval: (query) =>
      query.state.data?.some((r) => r.status === 'RUNNING') ? 3000 : false,
  });

  const iniciar = useMutation({
    mutationFn: () => iniciarRun(sourceId),
    onSuccess: () => void qc.invalidateQueries({ queryKey: chavesCmdb.runs(sourceId) }),
  });

  return (
    <div className="ml-4 mt-1 space-y-2 border-l border-border pl-4">
      <div className="flex items-center justify-between">
        <p className="text-xs font-medium text-muted-foreground">Histórico de runs</p>
        <Button size="sm" variant="secondary" disabled={iniciar.isPending} onClick={() => iniciar.mutate()}>
          {iniciar.isPending ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
          ) : (
            <Play className="h-3.5 w-3.5" aria-hidden />
          )}
          <span className="ml-1.5">Executar agora</span>
        </Button>
      </div>

      {isPending ? (
        <Skeleton className="h-10 w-full rounded" />
      ) : !runs?.length ? (
        <p className="text-xs text-muted-foreground">Nenhum run ainda.</p>
      ) : (
        <ul className="space-y-1">
          {runs.map((run) => (
            <RunLinha key={run.id} run={run} />
          ))}
        </ul>
      )}
    </div>
  );
}

function RunLinha({ run }: { run: DiscoveryRun }) {
  return (
    <li className="flex flex-wrap items-center gap-2 rounded bg-muted/40 px-3 py-1.5 text-xs">
      <span className={STATUS_COR[run.status] ?? ''}>{STATUS_PT[run.status] ?? run.status}</span>
      <span className="text-muted-foreground">{formatarDataHora(run.startedAt)}</span>
      {run.status === 'COMPLETED' && (
        <>
          <span>↑{run.itemsCreated} criados</span>
          <span>~{run.itemsUpdated} atualizados</span>
          {run.itemsDrifted > 0 && (
            <span className="text-yellow-600 dark:text-yellow-400">⚠ {run.itemsDrifted} desvios</span>
          )}
        </>
      )}
      {run.error && (
        <span className="truncate text-destructive">{run.error.slice(0, 80)}</span>
      )}
    </li>
  );
}

interface NovaFonteFormProps {
  onSave: (dto: CreateSourceDto) => void;
  onCancel: () => void;
  salvando: boolean;
}

function NovaFonteForm({ onSave, onCancel, salvando }: NovaFonteFormProps) {
  const [nome, setNome] = useState('');
  const [tipo, setTipo] = useState<'ESXI' | 'WAZUH'>('ESXI');
  const [endpoint, setEndpoint] = useState('');
  const [usuario, setUsuario] = useState('');
  const [senha, setSenha] = useState('');

  const inputCls =
    'w-full rounded-lg border border-input/80 bg-muted/40 px-3 py-2 text-sm transition-colors placeholder:text-muted-foreground hover:bg-muted focus-visible:border-ring/40 focus-visible:bg-background focus-visible:outline-none focus-visible:shadow-glow';

  const handleSave = () => {
    onSave({
      name: nome,
      type: tipo,
      endpoint,
      credentials: { username: usuario, password: senha },
    });
  };

  return (
    <Card>
      <CardBody className="space-y-3 py-4">
        <h2 className="text-sm font-semibold">Nova fonte de descoberta</h2>
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="space-y-1">
            <label className="text-xs text-muted-foreground">Nome</label>
            <input className={inputCls} value={nome} onChange={(e) => setNome(e.target.value)} placeholder="vCenter produção" />
          </div>
          <div className="space-y-1">
            <label className="text-xs text-muted-foreground">Tipo</label>
            <select
              className={inputCls}
              value={tipo}
              onChange={(e) => setTipo(e.target.value as 'ESXI' | 'WAZUH')}
            >
              <option value="ESXI">ESXi / vCenter</option>
              <option value="WAZUH">Wazuh</option>
            </select>
          </div>
          <div className="space-y-1 sm:col-span-2">
            <label className="text-xs text-muted-foreground">Endpoint (URL base da API)</label>
            <input className={inputCls} value={endpoint} onChange={(e) => setEndpoint(e.target.value)} placeholder="https://vcenter.aptum.local" />
          </div>
          <div className="space-y-1">
            <label className="text-xs text-muted-foreground">Usuário</label>
            <input className={inputCls} value={usuario} onChange={(e) => setUsuario(e.target.value)} autoComplete="username" />
          </div>
          <div className="space-y-1">
            <label className="text-xs text-muted-foreground">Senha</label>
            <input className={inputCls} type="password" value={senha} onChange={(e) => setSenha(e.target.value)} autoComplete="current-password" />
          </div>
        </div>
        <div className="flex gap-2">
          <Button size="sm" disabled={!nome || !endpoint || !usuario || !senha || salvando} onClick={handleSave}>
            Salvar
          </Button>
          <Button size="sm" variant="ghost" onClick={onCancel}>
            Cancelar
          </Button>
        </div>
      </CardBody>
    </Card>
  );
}
